import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, InjectionToken, NgModule, inject } from '@angular/core';
import { API_BASE_URL, SessionDto } from '@data-access/bulk-operations-api';
import { WS_BASE_URL, WebSocketClient } from '@data-access/bulk-operations-ws';
import { STATIC_JSON_BASE_URL } from '@data-access/hrm-lookups';
import { FeatureFlagService, LD_CLIENT_SIDE_ID } from '@feature-flag';
import { OAuthModule } from 'angular-oauth2-oidc';
import { take } from 'rxjs';

import { AppStatusToastService } from '#core/services/app-status-toast.service';
import { WebSocketToasterService } from '#core/services/web-socket-toatster.service';

import { environment } from '../../environments/environment';
import { AuthorizationInterceptor } from './interceptors/authorization.interceptor';
import { CorrelationIdInterceptor } from './interceptors/correlation-id.interceptor';
import { GlobalErrorHandler } from './services/global-error-handler.service';
import { VismaOAuthService } from './services/visma-oauth.service';

export const USER_SESSION = new InjectionToken<SessionDto>('User session data.');

@NgModule({
  declarations: [],
  exports: [],
  imports: [CommonModule, OAuthModule.forRoot()],
  providers: [
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelationIdInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthorizationInterceptor,
      multi: true,
    },
    { provide: API_BASE_URL, useValue: environment.dataImportApiBaseUrl },
    { provide: STATIC_JSON_BASE_URL, useValue: environment.staticJsonApiBaseUrl },
    { provide: WS_BASE_URL, useValue: environment.wsBaseUrl },
    { provide: LD_CLIENT_SIDE_ID, useValue: environment.ldClientSideID },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class CoreModule {
  private featureFlagService = inject(FeatureFlagService);
  private wsClient = inject(WebSocketClient);
  // Service will initialize OAuth automatically in constructor
  private vismaOAuthService = inject(VismaOAuthService);

  private readonly wsToasterService = inject(WebSocketToasterService);
  private readonly appStatusToastService = inject(AppStatusToastService);

  constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true });

    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import it in the AppModule only');
    }

    this.featureFlagService
      .getFlagValue('log-ws-messages-to-console')
      .pipe(take(1))
      .subscribe((isLogingEnabled) => {
        if (isLogingEnabled) {
          this.wsClient.isLoggingEnabled.set(true);
        }
      });
  }
}
